import React, { useMemo } from 'react';
import { Box, Typography, List, CircularProgress } from '@mui/material';
import ContactItem from './ContactItem';
import { useChat } from '../../contexts/ChatContext';

const STATUS_ORDER = ['available', 'busy', 'away', 'offline'];

/**
 * Lista de contatos — painel esquerdo Dual-Pane (FASE 1).
 */
export default function ContactsList({
  contacts,
  presenceMap,
  loadingContacts,
  onSelectContact,
  statusLabels = {},
  searchTerm = '',
  activeChatId = null
}) {
  const { openConversations } = useChat() || {};

  const selectedContactId = useMemo(() => {
    if (!activeChatId || !openConversations) return null;
    const conv = openConversations.find((c) => c.id === activeChatId);
    return conv?.contact?.id ?? null;
  }, [activeChatId, openConversations]);

  const grouped = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    let list = contacts || [];
    if (term) {
      list = list.filter(
        (c) =>
          (c.nome || '').toLowerCase().includes(term) ||
          (c.email || '').toLowerCase().includes(term)
      );
    }
    const byStatus = { available: [], busy: [], away: [], offline: [] };
    list.forEach((c) => {
      const s = presenceMap[c.id]?.status || c.status || 'offline';
      if (byStatus[s]) byStatus[s].push(c);
      else byStatus.offline.push(c);
    });
    return byStatus;
  }, [contacts, presenceMap, searchTerm]);

  if (loadingContacts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, bgcolor: 'background.paper' }}>
      {STATUS_ORDER.map((statusKey) => {
        const items = grouped[statusKey] || [];
        if (items.length === 0) return null;
        const label = statusLabels[statusKey] || statusKey;
        return (
          <Box key={statusKey} sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 0.5,
                display: 'block',
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: 11
              }}
            >
              {label}
            </Typography>
            <List disablePadding>
              {items.map((contact) => (
                <ContactItem
                  key={contact.id}
                  contact={contact}
                  presenceStatus={presenceMap[contact.id]?.status}
                  onClick={onSelectContact}
                  selected={selectedContactId === contact.id}
                />
              ))}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}
